// @ts-strict-ignore
import { KeyMap } from '../ccc-algorithm/key-map';
import { Resource } from '../ccc-algorithm/resource/resource';
import { Stock } from '../ccc-algorithm/stock/stock';
import { Variable } from '../ccc-algorithm/variable/variable';
import { EvaluatedFactor, Factor } from '../model/factor';
import { isEvaluatedFactor } from '../model/is-evaluated-factor';
import { findVariablesInStringFunction } from './expression-util';

export interface FactorValueOptions {
  bau: boolean;
  t?: number;
  weight?: number;
  unknownX?: number;
  atocLhs?: EvaluatedFactor;
  atocRhs?: EvaluatedFactor;
  costRelatedCallbacks?: {
    stockInflow: () => number;
    stock: () => number;
    operations: () => number;
    energy: () => number;
    resourceNativeAmount: () => number;
    emissions: () => number;
  };
}

export interface GetFactorValueData {
  variables: KeyMap<Variable>;
  resources?: KeyMap<Resource>;
  stocks?: KeyMap<Stock>;
}

export function getValueFromFactor(
  factor: EvaluatedFactor,
  variables: KeyMap<Variable>,
  options: FactorValueOptions = { bau: false }
): number {
  let outputFactor = 0;
  if (factor.expressionFunction) {
    const values = (<EvaluatedFactor>factor).expressionVariables.map((name) =>
      getValueFromVariableName(name, variables, outputFactor, options)
    );
    let expressionResult = (<EvaluatedFactor>factor).expressionFunction(...values);
    if (expressionResult !== Infinity) {
      outputFactor = expressionResult;
    }
  }
  return outputFactor;
}

export function instantiateFactor(factor: Factor | EvaluatedFactor): EvaluatedFactor {
  if (isEvaluatedFactor(factor)) {
    return factor;
  }
  let expressionFunction: Function = undefined;
  let expressionVariables: string[] = [];
  if (factor.expression !== undefined && factor.expression !== null) {
    expressionVariables = findVariablesInStringFunction(factor.expression);
    expressionFunction = Function(...expressionVariables, `return ${factor.expression}`);
  }
  return { ...factor, expressionFunction, expressionVariables };
}

export function getValueFromVariableName(
  name: string,
  variables: KeyMap<Variable>,
  fallback: number,
  options: FactorValueOptions
) {
  let value = fallback;
  const bau = options.bau;

  if (variables[name] !== undefined) {
    const variable = variables[name];
    value = bau ? variable.referenceValue : variable.value;
    value /= variable.unit === 'percent' ? 100 : 1;
  } else if (name === 'atoc_lhs') {
    value = getValueFromFactor(options.atocLhs, variables, options);
  } else if (name === 'atoc_rhs') {
    value = getValueFromFactor(options.atocRhs, variables, options);
  } else if (name === 't') {
    value = options.t;
  } else if (name === 'WEIGHT') {
    value = options.weight;
  } else if (name === 'unknown_x') {
    value = options.unknownX;
  } else if (name === 'stock_inflow') {
    value = options.costRelatedCallbacks.stockInflow();
  } else if (name === 'stock') {
    value = options.costRelatedCallbacks.stock();
  } else if (name === 'operations') {
    value = options.costRelatedCallbacks.operations();
  } else if (name === 'resource_native_amount') {
    value = options.costRelatedCallbacks.resourceNativeAmount();
  } else if (name === 'energy') {
    value = options.costRelatedCallbacks.energy();
  } else if (name === 'emissions') {
    value = options.costRelatedCallbacks.emissions();
  }
  return value;
}
